import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  SimpleGrid,
  Text,
  useDisclosure,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Textarea,
} from '@chakra-ui/react';
import { MdAssignment, MdCheckCircle, MdFileDownload, MdPendingActions } from 'react-icons/md';
import {
  listIndents,
  exportIndentsExcel,
  getIndent,
  assignIndentLine,
  releaseIndentLine,
  removeIndentRecommendation,
} from '../../../repositories/indent.repository';
import { getDashboardCounts } from '../../../repositories/dashboard.repository';
import { indentStatusToApi } from '../../../models/indent.model';
import { RecommendAssetsModal } from '../equipments/indents/RecommendAssetsModal';
import { RecommendPeopleModal } from '../equipments/indents/RecommendPeopleModal';
import {
  HubAmberButton,
  HubCard,
  HubError,
  HubFilterBar,
  HubGhostButton,
  HubKycBadge,
  HubLoading,
  HubPageHeader,
  HubPanel,
  HubRow,
  HubTable,
  hub,
} from './hubUi';

function isEquipmentLine(line) {
  const t = String(line?.lineType || line?.type || '').toLowerCase();
  return t === 'equipment' || Boolean(line?.equipmentTypeId || line?.equipmentType);
}

function assignBodyFromRec(rec) {
  if (rec.userEquipmentId != null) return { userEquipmentId: rec.userEquipmentId };
  if (rec.equipmentId != null) return { equipmentId: rec.equipmentId };
  if (rec.appUserId != null) return { appUserId: rec.appUserId };
  if (rec.vendorId != null) return { vendorId: rec.vendorId };
  return null;
}

export function HubIndentsHub({ push }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setData(await getDashboardCounts());
    } catch (e) {
      setError(e.message || 'Failed to load indent counts');
      toast({ title: e.message || 'Failed to load indent counts', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <HubLoading />;
  if (error) return <HubError message={error} onRetry={load} />;

  const indents = data?.indents || {};

  return (
    <>
      <HubPageHeader
        title="Indents"
        subtitle="Live indent requests. Hub-native list and detail with recommend / assign / release."
      />
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <HubCard
          icon={MdAssignment}
          label="All requests"
          value={String(indents.total ?? 0)}
          hint={`${indents.assigned ?? 0} assigned · ${indents.recommended ?? 0} recommended`}
          onClick={() => push({ id: 'indentList', label: 'All indents', filter: 'all' })}
        />
        <HubCard
          icon={MdPendingActions}
          label="Pending"
          value={String(indents.pending ?? 0)}
          hint="Needs attention →"
          onClick={() =>
            push({ id: 'indentList', label: 'Pending', filter: 'pending' })
          }
        />
        <HubCard
          icon={MdCheckCircle}
          label="Released"
          value={String(indents.released ?? 0)}
          hint="Completed →"
          onClick={() =>
            push({ id: 'indentList', label: 'Released', filter: 'released' })
          }
        />
      </SimpleGrid>
    </>
  );
}

export function HubIndentList({ push, filter = 'all', userId }) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(
    filter === 'pending' ? 'Pending' : filter === 'released' ? 'Released' : '',
  );
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listIndents({
        pageSize: 100,
        search: search.trim() || undefined,
        status: status ? indentStatusToApi(status) : undefined,
        userId: userId || undefined,
      });
      setItems(res.items || []);
    } catch (e) {
      toast({ title: e.message || 'Failed to load indents', status: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast, search, status, userId]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportIndentsExcel({
        search: search.trim() || undefined,
        status: status ? indentStatusToApi(status) : undefined,
      });
      toast({ title: 'Export downloaded', status: 'success' });
    } catch (e) {
      toast({ title: e.message || 'Export failed', status: 'error' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <HubPageHeader
        title="Indents list"
        subtitle="Live requests with Hub filters. Open a row for lines, recommendations, and assignment."
        actions={
          <HubGhostButton
            leftIcon={<MdFileDownload />}
            onClick={handleExport}
            isLoading={exporting}
          >
            Export Excel
          </HubGhostButton>
        }
      />
      <HubFilterBar
        search={search}
        setSearch={setSearch}
        searchPlaceholder="Search request, requester…"
        filters={[
          {
            key: 'status',
            value: status,
            onChange: setStatus,
            options: [
              { value: '', label: 'All status' },
              { value: 'Pending', label: 'Pending' },
              { value: 'Recommended', label: 'Recommended' },
              { value: 'Assigned', label: 'Assigned' },
              { value: 'Released', label: 'Released' },
              { value: 'In progress', label: 'In progress' },
            ],
          },
        ]}
        onClear={() => {
          setSearch('');
          setStatus('');
        }}
      />
      <HubTable
        loading={loading}
        rows={items}
        onRowClick={(r) =>
          push({
            id: 'indentDetail',
            label: r.requestNo || r.indentId || `REQ-${r.id}`,
            itemId: r.id,
          })
        }
        columns={[
          {
            key: 'requestNo',
            label: 'Request',
            render: (r) => r.requestNo || r.indentId || `REQ-${r.id}`,
          },
          {
            key: 'requester',
            label: 'Requester',
            render: (r) => r.requesterName || r.raisedByName || '—',
          },
          { key: 'itemCount', label: 'Items' },
          { key: 'summary', label: 'Summary' },
          {
            key: 'status',
            label: 'Status',
            render: (r) => <HubKycBadge status={r.status}>{r.status}</HubKycBadge>,
          },
        ]}
      />
    </>
  );
}

export function HubIndentDetail({ push, itemId }) {
  const toast = useToast();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeLineId, setActiveLineId] = useState(null);
  const assetsModal = useDisclosure();
  const peopleModal = useDisclosure();
  const releaseDisc = useDisclosure();
  const [releaseLine, setReleaseLine] = useState(null);
  const [releaseRemark, setReleaseRemark] = useState('');
  const [releaseRating, setReleaseRating] = useState(5);
  const [busy, setBusy] = useState(false);
  const cancelRef = React.useRef();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const row = await getIndent(itemId);
      if (!row) throw new Error('Indent not found');
      setRequest(row);
    } catch (e) {
      setError(e.message || 'Failed to load indent');
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => {
    load();
  }, [load]);

  const activeLine = useMemo(
    () => (request?.lines || []).find((l) => l.id === activeLineId) || null,
    [request, activeLineId],
  );

  const openRecommend = (line) => {
    setActiveLineId(line.id);
    if (isEquipmentLine(line)) assetsModal.onOpen();
    else peopleModal.onOpen();
  };

  const handleAssign = async (line, rec) => {
    const body = assignBodyFromRec(rec);
    if (!body) {
      toast({ title: 'Cannot assign this recommendation', status: 'warning' });
      return;
    }
    setBusy(true);
    try {
      const updated = await assignIndentLine(request.id, line.id, body);
      setRequest(updated);
      toast({ title: 'Assigned', status: 'success' });
    } catch (e) {
      toast({ title: e.message || 'Assign failed', status: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveRec = async (line, rec) => {
    setBusy(true);
    try {
      const updated = await removeIndentRecommendation(request.id, line.id, rec.id);
      setRequest(updated);
      toast({ title: 'Recommendation removed', status: 'success' });
    } catch (e) {
      toast({ title: e.message || 'Remove failed', status: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleRelease = async () => {
    if (!request?.id || !releaseLine?.id) return;
    if (!releaseRating) {
      toast({ title: 'Select a rating (1–5)', status: 'warning' });
      return;
    }
    setBusy(true);
    try {
      const updated = await releaseIndentLine(request.id, releaseLine.id, {
        releaseNote: releaseRemark.trim() || null,
        releaseRating: releaseRating || null,
      });
      setRequest(updated);
      releaseDisc.onClose();
      setReleaseLine(null);
      setReleaseRating(null);
      setReleaseRemark('');
      toast({ title: 'Line released', status: 'success' });
    } catch (e) {
      toast({ title: e.message || 'Release failed', status: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const recLabel = (rec) =>
    rec.assetName ||
    rec.personName ||
    rec.ownerName ||
    [rec.regNo, rec.equipmentType].filter(Boolean).join(' · ') ||
    rec.tradeName ||
    `Rec #${rec.id}`;

  if (loading) return <HubLoading />;
  if (error) return <HubError message={error} onRetry={load} />;
  if (!request) return <HubError message="Indent not found" />;

  const lines = request.lines || [];

  return (
    <>
      <HubPageHeader
        title={request.requestNo || request.indentId || `REQ-${request.id}`}
        subtitle={request.summary || 'Indent request'}
        actions={
          <HubKycBadge status={request.status}>{request.status}</HubKycBadge>
        }
      />

      <HubPanel title="Request summary">
        <HubRow label="Requester">
          {request.requesterName || request.raisedByName || '—'}
        </HubRow>
        <HubRow label="Email">{request.raisedByEmail}</HubRow>
        <HubRow label="Notes">{request.notes}</HubRow>
        <HubRow label="Items">{request.itemCount || lines.length}</HubRow>
        {request.appUserId ? (
          <Box mt={3}>
            <HubGhostButton
              onClick={() =>
                push({
                  id: 'memberDetail',
                  label: request.requesterName || request.raisedByName || 'Requester',
                  itemId: request.appUserId,
                })
              }
            >
              Open requester →
            </HubGhostButton>
          </Box>
        ) : null}
      </HubPanel>

      <Text
        mt={6}
        mb={3}
        fontSize="11px"
        fontWeight="700"
        letterSpacing="0.08em"
        textTransform="uppercase"
        color={hub.muted}
      >
        Lines
      </Text>

      <SimpleGrid columns={{ base: 1 }} spacing={4}>
        {lines.map((line) => (
          <HubPanel
            key={line.id}
            title={
              line.catalogLabel ||
              line.equipmentType ||
              line.specification ||
              `Line #${line.id}`
            }
            actions={
              <HubKycBadge status={line.status || request.status}>
                {line.status || request.status || '—'}
              </HubKycBadge>
            }
          >
            <HubRow label="Type">{line.lineType || line.type || '—'}</HubRow>
            <HubRow label="Qty">
              {[line.quantity, line.uom].filter(Boolean).join(' ') || '—'}
            </HubRow>
            <HubRow label="From">{line.requiredFrom || line.fromDate || '—'}</HubRow>
            <HubRow label="Till">{line.requiredTill || line.tillDate || '—'}</HubRow>
            <HubRow label="Remarks">{line.remarks || '—'}</HubRow>

            {line.assignment ? (
              <>
                <Text fontSize="13px" color={hub.muted} mt={3} mb={1}>
                  Assignment
                </Text>
                <HubRow label="Assigned">
                  {line.assignment.personName ||
                    line.assignment.regNo ||
                    line.assignment.assetId ||
                    '—'}
                </HubRow>
                <HubRow label="By">{line.assignment.assignedByDisplay || '—'}</HubRow>
                {line.assignment.releaseRating != null ? (
                  <HubRow label="Release rating">{line.assignment.releaseRating}</HubRow>
                ) : null}
                {line.assignment.releaseNote ? (
                  <HubRow label="Release note">{line.assignment.releaseNote}</HubRow>
                ) : null}
              </>
            ) : null}

            <Text fontSize="13px" color={hub.muted} mt={3} mb={2}>
              Recommendations ({(line.recommendations || []).length})
            </Text>
            {(line.recommendations || []).length ? (
              (line.recommendations || []).map((rec) => (
                <Flex
                  key={rec.id}
                  justify="space-between"
                  align="center"
                  gap={3}
                  py={2}
                  borderBottom="1px solid"
                  borderColor={hub.cardBorder}
                  flexWrap="wrap"
                >
                  <Box minW={0}>
                    <Text fontSize="14px" color={hub.text}>
                      {recLabel(rec)}
                    </Text>
                    {rec.remark || rec.note ? (
                      <Text fontSize="12px" color={hub.muted}>
                        {rec.remark || rec.note}
                      </Text>
                    ) : null}
                  </Box>
                  <Flex gap={2} flexWrap="wrap">
                    {rec.userEquipmentId ? (
                      <HubGhostButton
                        onClick={() =>
                          push({
                            id: 'equipmentDetail',
                            label: rec.regNo || rec.assetId || 'Equipment',
                            itemId: rec.userEquipmentId,
                          })
                        }
                      >
                        Open asset
                      </HubGhostButton>
                    ) : null}
                    {rec.appUserId ? (
                      <HubGhostButton
                        onClick={() =>
                          push({
                            id: 'memberDetail',
                            label: rec.personName || 'Member',
                            itemId: rec.appUserId,
                          })
                        }
                      >
                        Open member
                      </HubGhostButton>
                    ) : null}
                    <HubAmberButton
                      isDisabled={busy || Boolean(line.assignment)}
                      onClick={() => handleAssign(line, rec)}
                    >
                      Assign
                    </HubAmberButton>
                    <HubGhostButton
                      isDisabled={busy}
                      onClick={() => handleRemoveRec(line, rec)}
                    >
                      Remove
                    </HubGhostButton>
                  </Flex>
                </Flex>
              ))
            ) : (
              <Text fontSize="13px" color={hub.muted} mb={2}>
                No recommendations yet.
              </Text>
            )}

            <Flex gap={2} mt={3} flexWrap="wrap">
              <HubAmberButton onClick={() => openRecommend(line)}>
                Recommend
              </HubAmberButton>
              <HubGhostButton
                isDisabled={!line.assignment || Boolean(line.assignment?.releasedAt)}
                onClick={() => {
                  setReleaseLine(line);
                  setReleaseRemark('');
                  setReleaseRating(null);
                  releaseDisc.onOpen();
                }}
              >
                Release
              </HubGhostButton>
            </Flex>
          </HubPanel>
        ))}
      </SimpleGrid>

      <RecommendAssetsModal
        isOpen={assetsModal.isOpen}
        onClose={assetsModal.onClose}
        request={request}
        line={activeLine}
        onSaved={(updated) => {
          if (updated) setRequest(updated);
          else load();
        }}
        maxRecommendations={5}
      />
      <RecommendPeopleModal
        isOpen={peopleModal.isOpen}
        onClose={peopleModal.onClose}
        request={request}
        line={activeLine}
        onSaved={(updated) => {
          if (updated) setRequest(updated);
          else load();
        }}
        maxRecommendations={5}
      />

      <AlertDialog
        isOpen={releaseDisc.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={releaseDisc.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="bg.surface" color="text.primary" borderColor="border.subtle">
            <AlertDialogHeader>Release line</AlertDialogHeader>
            <AlertDialogBody>
              <Text mb={2} fontSize="13px" color={hub.muted}>
                Rating (1–5)
              </Text>
              <Flex gap={2} mb={3}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <HubGhostButton
                    key={n}
                    onClick={() => setReleaseRating(n)}
                    borderColor={releaseRating === n ? 'utkarsh.amber' : undefined}
                  >
                    {n}
                  </HubGhostButton>
                ))}
              </Flex>
              <Textarea
                value={releaseRemark}
                onChange={(e) => setReleaseRemark(e.target.value)}
                placeholder="Remark (optional)"
                bg="bg.input"
                borderColor="border.subtle"
                color="text.primary"
                _placeholder={{ color: 'text.placeholder' }}
              />
            </AlertDialogBody>
            <AlertDialogFooter gap={2}>
              <Button
                ref={cancelRef}
                size="sm"
                variant="outline"
                borderColor="border.subtle"
                color="text.primary"
                onClick={releaseDisc.onClose}
              >
                Cancel
              </Button>
              <HubAmberButton isLoading={busy} onClick={handleRelease}>
                Release
              </HubAmberButton>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
